/**
 * Portal logado — cliente e técnico veem seus próprios dados financeiros/operacionais.
 */
import { api } from "@/lib/api";

interface Page<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
}

export interface PortalIdentity {
  is_client: boolean;
  is_nerd: boolean;
  client_names: string[];
  nerd_display_name: string | null;
  nerd_status: string | null;
}

export interface PortalJob {
  id: string;
  code: string;
  title: string;
  status: string;
  urgency: string;
  scheduled_at: string | null;
  completed_at: string | null;
  category_name: string | null;
  city: string | null;
  price: string;
  nerd_payout: string;
  client_name: string | null;
  nerd_name: string | null;
}

export interface PortalClosing {
  id: string;
  year: number;
  month: number;
  status: string;
  gross_total: string;
  recognized_total: string;
  disputed_total: string;
  item_count: number;
  sent_at: string | null;
  closed_at: string | null;
  finalized_at: string | null;
  client_name: string | null;
  contract_name: string | null;
  created_at: string;
}

export interface PortalClosingItem {
  id: string;
  job_code: string;
  description: string;
  amount: string;
  scheduled_at: string | null;
  recognized: boolean;
  dispute_reason: string | null;
}

export interface PortalClosingDetail extends PortalClosing {
  items: PortalClosingItem[];
}

export interface PortalInvoice {
  id: string;
  code: string;
  status: string;
  contract_type: string;
  period_year: number | null;
  period_month: number | null;
  nf_deadline: string | null;
  services_total: string;
  reimbursement_total: string;
  total: string;
  scheduled_payment_date: string | null;
  paid_at: string | null;
  nf_submitted_at: string | null;
  nf_validated: boolean;
  advance_requested: boolean;
  sent_at: string | null;
  created_at: string;
}

export interface PortalInvoiceItem {
  id: string;
  job_code: string | null;
  description: string | null;
  service_amount: string;
  reimbursement_amount: string;
  total: string;
}

export interface PortalInvoiceDetail extends PortalInvoice {
  items: PortalInvoiceItem[];
}

export interface ClientSummary {
  open_jobs: number;
  completed_this_month: number;
  billing_estimate_month: string;
  pending_closings: number;
}

export interface NerdSummary {
  assigned_open: number;
  completed_this_month: number;
  to_receive: string;
  open_invoices: number;
  next_payment_date: string | null;
}

export const portalApi = {
  me: async (): Promise<PortalIdentity> => (await api.get("/v1/portal/me")).data,

  // Cliente
  clientSummary: async (): Promise<ClientSummary> =>
    (await api.get("/v1/portal/client/summary")).data,
  clientJobs: async (params: Record<string, unknown> = {}): Promise<Page<PortalJob>> =>
    (await api.get("/v1/portal/client/jobs", { params })).data,
  clientClosings: async (params: Record<string, unknown> = {}): Promise<Page<PortalClosing>> =>
    (await api.get("/v1/portal/client/closings", { params })).data,
  clientClosing: async (id: string): Promise<PortalClosingDetail> =>
    (await api.get(`/v1/portal/client/closings/${id}`)).data,
  clientReviewClosing: async (
    id: string,
    disputed: { item_id: string; reason: string | null }[],
  ): Promise<PortalClosingDetail> =>
    (await api.post(`/v1/portal/client/closings/${id}/review`, { disputed })).data,
  clientConfirmClosing: async (id: string): Promise<PortalClosingDetail> =>
    (await api.post(`/v1/portal/client/closings/${id}/confirm`)).data,

  // Técnico
  nerdSummary: async (): Promise<NerdSummary> =>
    (await api.get("/v1/portal/nerd/summary")).data,
  nerdJobs: async (params: Record<string, unknown> = {}): Promise<Page<PortalJob>> =>
    (await api.get("/v1/portal/nerd/jobs", { params })).data,
  nerdInvoices: async (params: Record<string, unknown> = {}): Promise<Page<PortalInvoice>> =>
    (await api.get("/v1/portal/nerd/invoices", { params })).data,
  nerdInvoice: async (id: string): Promise<PortalInvoiceDetail> =>
    (await api.get(`/v1/portal/nerd/invoices/${id}`)).data,
  nerdNfUploadUrl: async (
    id: string,
    filename: string,
    content_type: string,
  ): Promise<{ upload_url: string; storage_path: string; expires_at: string }> =>
    (await api.post(`/v1/portal/nerd/invoices/${id}/nf-upload-url`, { filename, content_type }))
      .data,
  nerdSubmitNf: async (
    id: string,
    filename: string,
    storage_path: string,
  ): Promise<PortalInvoiceDetail> =>
    (await api.post(`/v1/portal/nerd/invoices/${id}/submit-nf`, { filename, storage_path })).data,
};

export const JOB_STATUS_LABEL: Record<string, string> = {
  draft: "Rascunho",
  open: "Aberto",
  dispatched: "Em dispatch",
  assigned: "Atribuído",
  in_progress: "Em atendimento",
  completed: "Concluído",
  approved: "Aprovado",
  unproductive: "Improdutivo",
  cancelled: "Cancelado",
  expired: "Expirado",
};

export const JOB_STATUS_VARIANT: Record<string, string> = {
  draft: "muted",
  open: "info",
  dispatched: "info",
  assigned: "primary",
  in_progress: "warning",
  completed: "success",
  approved: "success",
  unproductive: "muted",
  cancelled: "danger",
  expired: "danger",
};

export const CLOSING_STATUS_LABEL: Record<string, string> = {
  draft: "Rascunho",
  sent: "Enviado",
  under_review: "Em conferência",
  closed_by_client: "Fechado",
  adjusted: "Ajustado",
  finalized: "Faturado",
  cancelled: "Cancelado",
};

export const CLOSING_STATUS_VARIANT: Record<string, string> = {
  draft: "muted",
  sent: "info",
  under_review: "warning",
  closed_by_client: "primary",
  adjusted: "purple",
  finalized: "success",
  cancelled: "danger",
};

export const INVOICE_STATUS_LABEL: Record<string, string> = {
  awaiting_nf: "Aguardando NF",
  reviewing: "Em análise",
  scheduled: "Programado",
  pending_fix: "Com pendência",
  rejected: "Recusada",
  paid: "Paga",
  cancelled: "Cancelada",
};

export const INVOICE_STATUS_VARIANT: Record<string, string> = {
  awaiting_nf: "warning",
  reviewing: "info",
  scheduled: "primary",
  pending_fix: "warning",
  rejected: "danger",
  paid: "success",
  cancelled: "muted",
};

export const MONTHS_PT = [
  "",
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];
